import React, { useMemo } from "react";
import { useTheme } from "@mui/material";
import { FiCheck, FiMinus } from "react-icons/fi";
import { SecondaryButton } from "../organisation/OrgModal";
import { Pill, Skeleton } from "./subscriptionUi";
import {
  addonPriceLabel,
  featureLabel,
  isShownFeature,
  formatLimit,
  METRICS,
  metricLabel,
  priceLabel,
} from "../../utils/subscriptionData";

// The one plan comparison table: pricing page, billing page, upgrade / change flow and the trial-ended
// prompt all render this. Rows come from whatever the API returns, so a new plan, limit, feature or
// add-on appears without a code change.
//
//   plans        public plans from GET /plans
//   currentPlan  the organisation's own plan object (highlights the column, marks custom limits)
//   renderAction (plan) => node, shown as the last row (Choose / Upgrade / Contact us)

const unionKeys = (plans, pick, preferred = []) => {
  const seen = new Set();
  plans.forEach((plan) => Object.keys(pick(plan) || {}).forEach((key) => seen.add(key)));
  const first = preferred.filter((key) => seen.has(key));
  return [...first, ...[...seen].filter((key) => !first.includes(key))];
};

const Mark = ({ on }) =>
  on ? (
    <>
      <FiCheck className="inline text-green-700" aria-hidden="true" />
      <span className="sr-only">Included</span>
    </>
  ) : (
    <>
      <FiMinus className="inline text-accent-gray-light" aria-hidden="true" />
      <span className="sr-only">Not included</span>
    </>
  );

const SkeletonTable = () => (
  <div className="space-y-3" role="status" aria-label="Loading plans">
    {[0, 1, 2, 3, 4, 5].map((row) => (
      <div key={row} className="grid grid-cols-4 gap-4">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-5" />
        <Skeleton className="h-5" />
        <Skeleton className="h-5" />
      </div>
    ))}
  </div>
);

const PlanComparison = ({ plans, loading = false, error = false, onRetry, currentPlan = null, renderAction, caption }) => {
  const theme = useTheme();
  const currentId = currentPlan?.plan_id;

  const metricKeys = useMemo(() => unionKeys(plans || [], (plan) => plan.limits, METRICS.map((m) => m.key)), [plans]);
  const featureKeys = useMemo(() => unionKeys(plans || [], (plan) => plan.features).filter(isShownFeature), [plans]);
  const addonKeys = useMemo(() => unionKeys(plans || [], (plan) => plan.addons), [plans]);

  if (loading) return <SkeletonTable />;

  if (error) {
    return (
      <div role="alert" className="p-4 border border-red-300 bg-red-50 text-red-900 rounded flex flex-wrap items-center justify-between gap-3">
        <span>We couldn't load the plans. Check your connection and try again.</span>
        {onRetry && <SecondaryButton onClick={onRetry}>Try again</SecondaryButton>}
      </div>
    );
  }

  if (!plans?.length) {
    return <p className="p-4 text-sm text-accent-gray-light border rounded" style={{ borderColor: theme.palette.secondary.v4 }}>No plans are available right now. Please check back soon.</p>;
  }

  const border = theme.palette.secondary.v4;
  const addonName = (key) => plans.find((plan) => plan.addons?.[key])?.addons[key].name || featureLabel(key);
  const yoursDiffers = (plan, key) =>
    Boolean(currentPlan?.custom_config) && plan.plan_id === currentId && currentPlan.base_limits && currentPlan.base_limits[key] !== plan.limits?.[key];

  const th = "text-left align-bottom font-semibold";
  const rowHead = "text-left font-medium p-2 md:p-3 min-w-[6.5rem] md:min-w-[9rem]";
  const cellFor = () => "p-2 md:p-3 min-w-[5.5rem] md:min-w-[9.5rem]";

  const GroupRow = ({ children }) => (
    <tr>
      <th scope="colgroup" colSpan={plans.length + 1} className="text-left px-2 pt-4 pb-2 md:px-3 md:pt-6 md:pb-2 text-[11px] md:text-xs uppercase tracking-wide text-accent-gray-light">
        {children}
      </th>
    </tr>
  );

  return (
    <div
      role="region"
      aria-label={caption || "Plan comparison, scrolls sideways on small screens"}
      tabIndex={0}
      className="shrink-0 overflow-x-auto overflow-y-hidden rounded-md border focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
      style={{ borderColor: border }}
    >
      <table className="w-full border-collapse text-xs md:text-sm">
        <caption className="sr-only">{caption || "Plan comparison"}</caption>
        <thead>
          <tr className="border-b" style={{ borderColor: border }}>
            <th scope="col" className={`${rowHead} align-bottom`}>
              <span className="sr-only">Feature</span>
            </th>
            {plans.map((plan) => (
              <th key={plan.plan_id} scope="col" className={`${th} ${cellFor()}`}>
                <div className="flex flex-wrap items-center gap-1 md:gap-2">
                  <span className="text-sm md:text-base">{plan.name}</span>
                  {plan.plan_id === currentId && <Pill tone="blue">Current plan</Pill>}
                </div>
                <div className="text-sm md:text-lg font-semibold mt-0.5 md:mt-1">{priceLabel(plan)}</div>
                {plan.description && <p className="hidden md:block text-xs font-normal text-accent-gray-light mt-1">{plan.description}</p>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <GroupRow>Monthly allowances</GroupRow>
          {metricKeys.map((key) => (
            <tr key={key} className="border-t" style={{ borderColor: border }}>
              <th scope="row" className={rowHead}>
                {metricLabel(key)}
              </th>
              {plans.map((plan) => {
                const value = plan.limits?.[key];
                const total = plan.limit_periods?.[key] === "total";
                return (
                  <td key={plan.plan_id} className={cellFor()}>
                    {value === null || value === undefined ? (plan.contact_sales ? "Custom" : formatLimit(value)) : formatLimit(value)}
                    {total && <span className="block text-xs text-accent-gray-light">for the whole term</span>}
                    {yoursDiffers(plan, key) && (
                      <span className="block text-xs text-violet-800">Your organisation: {formatLimit(currentPlan.base_limits[key])}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}

          {featureKeys.length > 0 && <GroupRow>Features included</GroupRow>}
          {featureKeys.map((key) => (
            <tr key={key} className="border-t" style={{ borderColor: border }}>
              <th scope="row" className={rowHead}>
                {featureLabel(key)}
              </th>
              {plans.map((plan) => (
                <td key={plan.plan_id} className={cellFor()}>
                  <Mark on={Boolean(plan.features?.[key])} />
                </td>
              ))}
            </tr>
          ))}

          {addonKeys.length > 0 && <GroupRow>Add-on pricing</GroupRow>}
          {addonKeys.map((key) => (
            <tr key={key} className="border-t" style={{ borderColor: border }}>
              <th scope="row" className={rowHead}>
                {addonName(key)}
              </th>
              {plans.map((plan) => {
                const addon = plan.addons?.[key];
                return (
                  <td key={plan.plan_id} className={cellFor()}>
                    {addon ? (
                      <>
                        {addonPriceLabel(addon)}
                        {addon.units > 1 && <span className="block text-xs text-accent-gray-light">{formatLimit(addon.units)} per pack</span>}
                      </>
                    ) : (
                      <span className="text-accent-gray-light">—<span className="sr-only">Not available</span></span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}

          {renderAction && (
            <tr className="border-t" style={{ borderColor: border }}>
              <th scope="row" className={rowHead}>
                <span className="sr-only">Action</span>
              </th>
              {plans.map((plan) => (
                <td key={plan.plan_id} className={`${cellFor()} py-3 md:py-4`}>
                  {renderAction(plan)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PlanComparison;
